import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useQuery, gql, useMutation } from '@apollo/client';
import { Web3Context } from './context/Web3Context';
import Header from './components/Header';
import Feed from './components/Feed';
import Library from './components/Library';
import CreatorPage from './components/CreatorPage';
import ProtectedRoute from './components/ProtectedRoute';
import styled, { keyframes } from 'styled-components';
import Home from './components/Home';
import Navigation from './components/Navigation';
import ContentWrapper from './components/ContentWrapper';
import PublicRoute from './components/PublicRoute';
import NotFound from './components/NotFound';
import Loading from './components/Loading';
import { checkNFT } from './utils/checkNFT';
import { getCookie, removeCookie, setCookie } from './utils/cookies';

const HELLO_QUERY = gql`
  query {
    hello
  }
`;

const GET_USER_QUERY = gql`
  query getUser($account: String!) {
    getUser(account: $account) {
      id
      account
      name
      email
      nftOwned
      isCreator
    }
  }
`;

const UPDATE_NFT_STATUS_MUTATION = gql`
  mutation UpdateNFTStatus($account: String!, $name: String, $email: String, $nftOwned: Boolean, $isCreator: Boolean) {
    updateUser(account: $account, name: $name, email: $email, nftOwned: $nftOwned, isCreator: $isCreator) {
      id
      account
      name
      email
      nftOwned
      isCreator
    }
  }
`;


const AUTHENTICATE_USER_MUTATION = gql`
  mutation AuthenticateUser($address: String!, $signature: String!, $message: String!) {
    authenticateUser(address: $address, signature: $signature, message: $message)
  }
`;


const gradientAnimation = keyframes`
  0% {
    background-position: 0% 0%;
  }
  50% {
    background-position: 100% 100%;
  }
  100% {
    background-position: 0% 0%;
  }
`;

const AppContainer = styled.div`
  font-family: ${({ theme }) => theme.fonts.main};
  text-align: center;
  background-color: ${({ theme }) => theme.colors.background};
  width: 100%;
  height: 100vh;
  background: linear-gradient(33deg, #656f8a, #f0f0f0, #abb3b2, #3498db);
  background-size: 400% 400%;
  animation: ${gradientAnimation} 15s ease infinite;
  display: flex;
  flex-direction: column;
`;

const PublicPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: ${({ theme }) => theme.colors.publicBackground};
  height: calc(100vh - 70px);
  text-align: center;
`;

const ProtectedPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.protectedBackground};
  height: calc(100vh - 70px);
  text-align: center;
`;

const ConnectWalletButton = styled.button`
  margin: 10px;
  padding: 10px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const App = () => {
  const { account, setAccount, userInfo, setUserInfo, setUserAuth, walletConnected } = useContext(Web3Context);
  const [showUpdate, setShowUpdate] = useState(false);
  const [appLoading, setAppLoading] = useState(true);
  const [isCreator, setIsCreator] = useState(false);
  const [hasNFT, setHasNFT] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [nftCheckLoading, setNftCheckLoading] = useState(true);
  const TOKEN_ID = 1;

  const { data: helloData, loading: helloLoading, error: helloError } = useQuery(HELLO_QUERY);

  useEffect(() => {
    if (helloData && helloData.hello) {
      setAppLoading(false);
    } else if (helloError) {
      setAppLoading(false);
    }
  }, [helloData, helloLoading, helloError]);

  const { data: userData, loading: userLoading, error: userError, refetch } = useQuery(GET_USER_QUERY, {
    variables: { account },
    skip: !account || !isAuthenticated,
    onCompleted: (data) => {
      if (data && data.getUser) {
        setUserInfo({
          name: data.getUser.name || '',
          email: data.getUser.email || '',
        });
        setHasNFT(data.getUser.nftOwned || false);
        setIsCreator(data.getUser.isCreator || false);
      } else {
        setUserInfo({ name: '', email: '' });
        setHasNFT(false);
        setIsCreator(false);
      }
      setAppLoading(false);
    },
    onError: () => {
      setUserInfo({ name: '', email: '' });
      setHasNFT(false);
      setIsCreator(false);
      setAppLoading(false);
    }
  });

  const [updateNFTStatus] = useMutation(UPDATE_NFT_STATUS_MUTATION);
  const [authenticateUser] = useMutation(AUTHENTICATE_USER_MUTATION);

  const checkNFTStatus = async () => {
    try {
      const ownsNFT = await checkNFT(TOKEN_ID);
      setHasNFT(ownsNFT);
    } catch (error) {
      console.error('Error checking NFT status:', error);
    } finally {
      setNftCheckLoading(false);
    }
  };

  useEffect(() => {
    if (account && isAuthenticated) {
      checkNFTStatus();
    } else {
      setNftCheckLoading(false);
    }
  }, [account, isAuthenticated]);

  const handleAuthClick = async () => {
    try {
      if (window.ethereum) {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        console.log('Connected to Ethereum:', accounts);
        const message = 'Sign this message to authenticate';
        const signature = await window.ethereum.request({
          method: 'personal_sign',
          params: [message, accounts[0]],
        });

        const { data } = await authenticateUser({
          variables: {
            address: accounts[0],
            signature,
            message,
          },
        });

        if (data.authenticateUser && data.authenticateUser.length > 0) {
          setCookie('token', data.authenticateUser, 1);
          setUserAuth(true);
          setAccount(accounts[0]);
          setIsAuthenticated(true);
        }
      } else {
        console.error('No Ethereum provider found');
      }
    } catch (error) {
      if (error.code === 4001) {
        console.error('User denied account authorization');
      } else {
        console.error('Authentication failed:', error);
        removeCookie('token');
      }
    }
  };

  const handleUpdateUser = async () => {
    try {
      await updateNFTStatus({
        variables: {
          account,
          name: userInfo.name,
          email: userInfo.email,
          nftOwned: hasNFT,
          isCreator: isCreator,
        },
      });
    } catch (error) {
      console.error('Error updating NFT status:', error);
    }
  };

  return (
    <Router>
      <AppContainer>
        {isAuthenticated ? (
          <>
            <Header
              account={account}
              userInfo={userInfo}
              showUpdate={showUpdate}
              setShowUpdate={setShowUpdate}
              title={helloData?.hello ? helloData.hello : 'PLANETARIA RADIO'}
            />
            <ContentWrapper>
              {nftCheckLoading ? (
                <Loading />
              ) : (
                <>
                  <Routes>
                    <Route path="/feed" element={account ? <ProtectedRoute component={Feed} /> : <Navigate to="/" />} />
                    <Route path="/library" element={account ? <ProtectedRoute component={Library} /> : <Navigate to="/" />} />
                    <Route path="/home" element={<PublicRoute component={Home} />} />
                    <Route path="/creator" element={isCreator ? <ProtectedRoute component={CreatorPage} /> : <NotFound />} />
                    <Route path="/" element={
                      !account ? (
                        <PublicPageContainer>
                          <p>Please connect your Wallet to continue.</p>
                          <ConnectWalletButton onClick={handleAuthClick}>Continue with Wallet</ConnectWalletButton>
                        </PublicPageContainer>
                      ) : (
                        <ProtectedPageContainer>
                          {userError && <p>Error: {userError.message}</p>}
                          {userLoading && <Loading />}
                          {hasNFT ? (
                            <Home />
                          ) : (
                            <Home />
                          )}
                        </ProtectedPageContainer>
                      )
                    } />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  {account && <Navigation isCreator={isCreator} />}
                </>
              )}
            </ContentWrapper>
          </>
        ) : (
          <PublicPageContainer>
            <p>Please connect your Wallet to continue.</p>
            <ConnectWalletButton onClick={handleAuthClick}>Continue with Wallet</ConnectWalletButton>
          </PublicPageContainer>
        )}
      </AppContainer>
    </Router>
  );
};

export default App;
